// User roles
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  CONTRACTOR: 'contractor',
  EMPLOYEE: 'employee',
};

// Project statuses (must match the projects.status enum in the database)
export const PROJECT_STATUSES = {
  OPEN: 'open',
  ACTIVE: 'active',
  COMPLETE: 'complete',
  INVOICE_SENT: 'invoice_sent',
  PAID: 'paid',
  CANCELLED: 'cancelled',
};

// Project priorities
export const PROJECT_PRIORITIES = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent',
};

// Invoice statuses
export const INVOICE_STATUSES = {
  DRAFT: 'draft',
  SENT: 'sent',
  PAID: 'paid',
  OVERDUE: 'overdue',
};

// Billing types
export const BILLING_TYPES = {
  HOURLY: 'hourly',
  FIXED: 'fixed',
  RETAINER: 'retainer',
};

// Role configuration with colors and labels
export const ROLE_CONFIG = {
  [USER_ROLES.ADMIN]: {
    label: 'Administrator',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
  },
  [USER_ROLES.MANAGER]: {
    label: 'Manager',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
  },
  [USER_ROLES.CONTRACTOR]: {
    label: 'Contractor',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
  },
  [USER_ROLES.EMPLOYEE]: {
    label: 'Employee',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
  },
};

// Status configuration with colors
export const STATUS_CONFIG = {
  [PROJECT_STATUSES.OPEN]: {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
  },
  [PROJECT_STATUSES.ACTIVE]: {
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
  },
  [PROJECT_STATUSES.COMPLETE]: {
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
  },
  [PROJECT_STATUSES.INVOICE_SENT]: {
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800',
  },
  [PROJECT_STATUSES.PAID]: {
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-800',
  },
  [PROJECT_STATUSES.CANCELLED]: {
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
  },
};

// Navigation items based on user roles
export const getNavigationItems = (userRole) => {
  const baseItems = [
    {
      key: 'dashboard',
      name: 'Dashboard',
      href: '/dashboard',
      icon: 'LayoutDashboard',
    },
    {
      key: 'timeTracking',
      name: 'Time Tracking',
      href: '/time',
      icon: 'Clock',
    },
    {
      key: 'projects',
      name: 'Projects',
      href: '/projects',
      icon: 'FolderOpen',
    },
    {
      key: 'estimates',
      name: 'Estimates',
      href: '/estimates',
      icon: 'Calculator',
    },
  ];

  const managerItems = [
    {
      key: 'clients',
      name: 'Clients',
      href: '/clients',
      icon: 'Users',
    },
    {
      key: 'billing',
      name: 'Billing',
      href: '/billing',
      icon: 'DollarSign',
    },
  ];

  const teamItem = [
    {
      key: 'team',
      name: 'Team',
      href: '/team',
      icon: 'Users',
    },
  ];

  if ([USER_ROLES.ADMIN, USER_ROLES.MANAGER].includes(userRole)) {
    return [...baseItems, ...managerItems, ...teamItem];
  }

  if (userRole === USER_ROLES.CONTRACTOR) {
    return [...baseItems, ...managerItems];
  }

  return baseItems;
};

// API endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  PROFILE: '/auth/profile',
  CHANGE_PASSWORD: '/auth/change-password',

  // Dashboard
  DASHBOARD: '/dashboard',

  // Clients
  CLIENTS: '/clients',
  CLIENT_DETAIL: (id) => `/clients/${id}`,
  CLIENT_PROJECTS: (id) => `/clients/${id}/projects`,
  CLIENT_TIME: (id) => `/clients/${id}/time`,
  CLIENT_INVOICES: (id) => `/clients/${id}/invoices`,

  // Projects
  PROJECTS: '/projects',
  PROJECT_DETAIL: (id) => `/projects/${id}`,
  PROJECT_TASKS: (id) => `/projects/${id}/tasks`,
  PROJECT_TIME: (id) => `/projects/${id}/time`,

  // Time tracking
  TIME_ENTRIES: '/time',
  TIME_START: '/time/clock-in',
  TIME_STOP: '/time/clock-out',
  TIME_ACTIVE: '/time/active',
  TIME_EXPORT: '/time/export',

  // Billing
  INVOICES: '/billing/invoices',
  INVOICE_DETAIL: (id) => `/billing/invoices/${id}`,
  INVOICE_SEND: (id) => `/billing/invoices/${id}/send`,
  INVOICE_MARK_PAID: (id) => `/billing/invoices/${id}/mark-paid`,
  INVOICE_DOWNLOAD: (id) => `/billing/invoices/${id}/download`,
  BILLING_SUMMARY: '/billing/summary',

  // Estimates
  ESTIMATES: '/estimates',
  ESTIMATE_DETAIL: (id) => `/estimates/${id}`,
  ESTIMATE_RUN: (id) => `/estimates/${id}/estimate`,
  ESTIMATE_VALUATE: (id) => `/estimates/${id}/valuate`,
  ESTIMATE_PDF: (id) => `/estimates/${id}/pdf`,

  // Settings (admin)
  AI_SETTINGS: '/settings/ai',
  AI_SETTINGS_TEST: '/settings/ai/test',
};

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM d, yyyy',
  DISPLAY_WITH_TIME: 'MMM d, yyyy h:mm a',
  INPUT: 'yyyy-MM-dd',
  INPUT_WITH_TIME: "yyyy-MM-dd'T'HH:mm",
  TIME_ONLY: 'h:mm a',
};

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};

// File upload limits
export const FILE_UPLOAD = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
};

// Timer settings
export const TIMER_SETTINGS = {
  UPDATE_INTERVAL: 1000, // 1 second
  IDLE_WARNING_TIME: 5 * 60 * 1000, // 5 minutes
  AUTO_STOP_TIME: 15 * 60 * 1000, // 15 minutes
};

// Currency settings
export const CURRENCY = {
  DEFAULT: 'USD',
  SYMBOL: '$',
  DECIMAL_PLACES: 2,
};

// Time formats
export const TIME_FORMATS = {
  DECIMAL: 'decimal', // 1.5 hours
  HOURS_MINUTES: 'hm', // 1h 30m
  TOTAL_MINUTES: 'minutes', // 90 minutes
};

// Default values
export const DEFAULTS = {
  HOURLY_RATE: 0,
  PROJECT_STATUS: PROJECT_STATUSES.ACTIVE,
  PROJECT_PRIORITY: PROJECT_PRIORITIES.MEDIUM,
  INVOICE_STATUS: INVOICE_STATUSES.DRAFT,
  USER_ROLE: USER_ROLES.EMPLOYEE,
};

// Validation rules
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 6,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 50,
  EMAIL_MAX_LENGTH: 255,
  DESCRIPTION_MAX_LENGTH: 1000,
  TASK_TITLE_MAX_LENGTH: 200,
};

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'timeclock_auth_token',
  REFRESH_TOKEN: 'timeclock_refresh_token',
  USER_PREFERENCES: 'timeclock_user_preferences',
  THEME: 'timeclock_theme',
};

// Theme options
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// Notification types
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Export all constants as default
export default {
  USER_ROLES,
  PROJECT_STATUSES,
  PROJECT_PRIORITIES,
  INVOICE_STATUSES,
  BILLING_TYPES,
  ROLE_CONFIG,
  STATUS_CONFIG,
  getNavigationItems,
  API_ENDPOINTS,
  DATE_FORMATS,
  PAGINATION,
  FILE_UPLOAD,
  TIMER_SETTINGS,
  CURRENCY,
  TIME_FORMATS,
  DEFAULTS,
  VALIDATION,
  STORAGE_KEYS,
  THEMES,
  NOTIFICATION_TYPES,
};